import { all, takeLatest, put, call } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import notifications from 'src/redux/notifications/actions';
import { loadValidation, publishCard } from '../api';
import { toValidation } from '../findings';
import { RATE_CARD_HTTP } from '../constants';
import actions from './actions';
import actionTypes from './actionTypes';
import type { LoadValidationRequestAction, PublishRequestAction } from './actions.d';

/**
 * Publishes the card. The page navigates off `publishedId` landing in the store rather than off a
 * callback, which would fire whatever the user did meanwhile and drag a leaver back into the card.
 */
export function* publishRequest({ payload }: PublishRequestAction): Generator<any, void, any> {
  try {
    // `sendRequest` never re-throws: with `preserveErrorStatus` a 422 comes back as the result. No
    // errorType, because a publish rejection belongs in the page's own banner, not a toast.
    const result = yield call(sendRequest, publishCard, { id: payload.id }, undefined, undefined, undefined, {
      preserveErrorStatus: true,
    });

    const succeeded = typeof result?.status === 'number' && result.status >= 200 && result.status < 300;

    if (succeeded && result.data?.id) {
      yield put(actions.publishSuccess({ cardId: result.data.id }));
    } else if (result?.status === RATE_CARD_HTTP.CONFLICT) {
      // A publish is already running. Nothing to correct — the same request will work shortly.
      yield put(actions.publishError({ errors: [], conflict: true }));
    } else {
      // Publish failures are card-level, so they arrive under `base` rather than keyed by field. A refusal
      // carrying a sentence but no validations map still has to reach the banner, or nothing is actionable.
      const errors: string[] = result?.validations?.base ?? (result?.message ? [result.message] : []);

      if (errors.length === 0) {
        yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
      }

      yield put(actions.publishError({ errors }));
    }
  } catch (error) {
    logger.error(error);
    yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
    yield put(actions.publishError({ errors: [] }));
  }
}

/**
 * Reads the blockers publish would raise, so the page can show them instead of promising a success it
 * cannot vouch for. A failure here is not fatal — publish re-validates — so it only sets the flag.
 */
export function* loadValidationRequest({ payload }: LoadValidationRequestAction): Generator<any, void, any> {
  try {
    // No errorType for the generic failures: the page renders those itself. (A 401 or 403 still goes
    // through sendRequest's own unconditional branches, which is a helper-level problem.)
    const result = yield call(sendRequest, loadValidation, { id: payload.id });
    const data = result?.status === 200 ? result.data?.success?.data : null;

    // The array, not the envelope: `{}` is truthy and would resolve as "nothing found", which reads as
    // a clean card. A moved contract must not be indistinguishable from a card with no problems.
    if (Array.isArray(data?.findings)) {
      yield put(actions.loadValidationSuccess(toValidation(data)));

      return;
    }

    logger.error(new Error(`rate card ${payload.id}: validation answered without findings[]`));
    yield put(actions.loadValidationError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadValidationError());
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.PUBLISH_REQUEST, publishRequest),
    // `takeLatest`: a re-check after a corrected file supersedes the one it corrects, and the older
    // answer landing second would put the blockers the user just fixed back on screen.
    takeLatest(actionTypes.LOAD_VALIDATION_REQUEST, loadValidationRequest),
  ]);
}
